import React from "react";
import toast from "react-hot-toast";
import { networkLabel, NETWORKS, validateAddress } from "rujira.js";
import { Button, NetworkIcon, Provider } from "rujira.ui";
import {
  Copy,
  External,
  LinkConnect,
  LinkDisconnect,
} from "rujira.ui/src/components/icons/Icons";
import { useAccounts } from "../../services/accounts";

export type WalletOptionType = {
  description: string;
  links: {
    url: string;
    icon: React.ReactNode;
  }[];
  logo: JSX.Element;
  provider?: Provider.Key;
};

export const WalletOption = ({
  description,
  links,
  logo,
  provider,
}: WalletOptionType) => {
  const { isAvaialable, accounts, connect, disconnect } = useAccounts();
  const connected =
    provider && accounts?.filter((a) => a.provider === provider);
  const addresses = [...new Set(connected?.map((x) => x.address))].sort(
    (a, b) => {
      if (a.startsWith("thor") || a.startsWith("sthor")) return -1;
      if (b.startsWith("thor") || b.startsWith("sthor")) return 1;
      return a.localeCompare(b);
    }
  );

  const duplicate = false;

  return (
    <div className="col-12 col-md-6 col-lg-4">
      <div className="card h-full flex p-3 dir-c ai-s portfolio__wallet">
        <div className="portfolio__wallet-logo">{logo}</div>
        {provider && isAvaialable(provider) ? (
          <Button
            className="button--sm mt-2 button--outline"
            disabled={!isAvaialable(provider)}
            label={connected?.length ? "Disconnect" : "Connect"}
            onClick={() =>
              connected?.length ? disconnect(provider) : connect(provider)
            }>
            {connected?.length ? <LinkDisconnect /> : <LinkConnect />}
          </Button>
        ) : (
          <Button
            as={"a"}
            className="button--sm mt-2 button button--grey button--outline button--icon-right"
            href={links[0].url}
            label="Install"
            target="_blank">
            <External />
          </Button>
        )}
        {addresses && addresses?.length > 0 ? (
          <div className="flex dir-c gap-y-1 w-full mt-2">
            {addresses?.map((x) => {
              return (
                <div
                  key={x}
                  className="flex jc-sb ai-c w-full color-grey hover-white">
                  <div className="flex">
                    {NETWORKS.map((n) =>
                      validateAddress(n, x) ? (
                        <div
                          data-tooltip-content={networkLabel(n)}
                          data-tooltip-id="global-tip"
                          style={{ marginRight: 2 }}>
                          <NetworkIcon
                            className="w-2 h-2"
                            key={n}
                            network={n}
                          />
                        </div>
                      ) : null
                    )}
                  </div>
                  <div className="flex ai-c gap-1">
                    <small
                      onClick={() => {
                        toast.success("Address copied to clipboard");
                        navigator.clipboard.writeText(x);
                      }}
                      data-tooltip-content={x}
                      data-tooltip-id="global-tip"
                      className="font-sm mono flex aic gap-0.5 pointer">
                      {x.slice(0, 10)}...{x.slice(x.length - 5, x.length)}
                      <Copy className="w-2 h-2" />
                    </small>
                    {duplicate && (
                      <span
                        className="tag tag--sm"
                        data-tooltip-content="The address exists on multiple wallets, this is the default signer wallet"
                        data-tooltip-id="global-tip">
                        default
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex mt-1">
            {provider &&
              Provider.signer(provider)
                .networks()
                .sort((a, b) => {
                  if (a === "THOR") return -1;
                  if (b === "THOR") return 1;

                  return networkLabel(a).localeCompare(networkLabel(b));
                })
                .map((n) => (
                  <NetworkIcon network={n} key={n} className="w-2 h-2 mr-0.5" />
                ))}
          </div>
        )}
        {connected?.length ? null : (
          <>
            <p>{description}</p>
            <div className="portfolio__wallet-links mt-a">
              {links.map((l) => (
                <a key={l.url} href={l.url} target="_blank">
                  {l.icon}
                </a>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
